<template>
    <Modal class="new-order-waivers-modal"
           icon="fa-file-signature"
           :shaded-body="true"
           title="NEW_ORDER_WAIVERS_MODAL.MODAL_TITLE"
           size="lg">
        <p class="pa-1">
            {{ l('WAIVERS.ORDER_CONTAINS_WAIVERS') }}
            {{ l('WAIVERS.PROMPT_TO_COMPLETE') }}
        </p>

        <div v-if="waiversPending"
             class="text-center pa-1">
            <i class="fa-solid fa-spinner fa-spin"></i>
            &nbsp;
            <small><strong>{{ l('WAIVERS.WAIT_FOR_GENERATION') }}</strong></small>
        </div>
        <template v-else>
            <Waiver v-for="waiver in waivers"
                    :key="waiver.ID"
                    :waiver="waiver"></Waiver>
        </template>

        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn color="default"
                           size="lg"
                           class="cancel-btn"
                           @click="close">
                    {{ l('CLOSE') }}
                </SquareBtn>

                <SquareBtn color="default"
                           size="lg"
                           @click="reloadWaiverStatus">
                    <i class="fa-solid fa-arrows-rotate"
                       :class="[loadingWaivers ? 'fa-spinner fa-spin' : 'fa-arrows-rotate']"></i>
                    {{ l('WAIVERS.REFRESH') }}
                </SquareBtn>

                <SquareBtn color="info"
                           size="lg"
                           @click="ok">
                    {{ l('NEW_ORDER_WAIVERS_MODAL.VIEW_ORDER__BTN') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import Modal from '@/vue/components/Modals/Modal';
import SquareBtn from '@/vue/components/SquareBtn';
import Waiver from '@/epos/vue/pages/order-details/components/Waiver';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    components: {
        Modal,
        SquareBtn,
        Waiver,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        order: {
            type: Object,
            required: true,
        },

        initialOrderWaiverStatus: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            /**
             * Copy this from the prop so that we can change the data.
             *
             * @type {OrderWaiverStatus}}
             */
            orderWaiverStatus: this.initialOrderWaiverStatus,

            /**
             * Interval used to re-check the waiver status until waivers are generated.
             */
            reloadInterval: null,

            loadingWaivers: false,
        };
    },

    computed: {
        waiversPending() {
            return this.orderWaiverStatus.waiversPending();
        },

        /**
         * @return {Waiver[]}
         */
        waivers() {
            /**
             * @type {OrderWaiverStatus}}
             */
            const orderWaiverStatus = this.orderWaiverStatus;
            if (orderWaiverStatus.existingWaivers?.length > 0) {
                return orderWaiverStatus.existingWaivers;
            }

            return [];
        },
    },

    created() {
        if (!this.orderWaiverStatus || this.orderWaiverStatus.waiversPending()) {
            // The waivers are still pending when the modal was loaded.
            // Start an interval to check if they're ready every 3 seconds.
            // The interval will be cleared in the reloadWaiverStatus function when they become ready.
            this.reloadInterval = setInterval(() => {
                this.reloadWaiverStatus();
            }, 3000);
        }
    },

    beforeUnmount() {
        if (this.reloadInterval) {
            clearInterval(this.reloadInterval);
        }
    },

    methods: {
        /**
         * @return {Promise<OrderWaiverStatus>}
         */
        reloadWaiverStatus() {
            console.debug('Reloading order waiver status for order', this.order.ID);
            this.loadingWaivers = true;
            return container.waiversService
                .getOrderWaiverStatus(this.order.ID)
                .then((orderWaiverStatus) => {
                    this.orderWaiverStatus = orderWaiverStatus;

                    if (!orderWaiverStatus.waiversPending()) {
                        clearInterval(this.reloadInterval);
                    }

                    return orderWaiverStatus;
                })
                .finally(() => {
                    this.loadingWaivers = false;
                });
        },

        close() {
            this.resolve();
        },

        ok() {
            container.navigationService.viewOrderDetails(this.order.bookingRef);
            this.resolve();
        },
    },
};
</script>

<style lang="less">
@import (less, reference) "../../../../less/variables.less";

.new-order-waivers-modal {
    .waiver {
        background: @white;

        &:not(:last-child) {
            margin-bottom: @gutter-width;
        }
    }

    .waiver__forms {
        background: rgba(0, 0, 0, 0.05);
    }

    .waiver__forms-toggle {
        background: rgba(0, 0, 0, .05);
    }
}
</style>
